const API_BASE = import.meta.env.VITE_API_URL || ''
const IMAGES_PATH = '/api/signatures/images'

/** Formati che il backend accetta (riconosciuti dal contenuto): niente SVG (#272). */
export const SIGNATURE_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp']

/**
 * Carica un'immagine di firma su `/api/signatures/images` e restituisce l'URL
 * da usare come src. All'invio il backend la allega come CID.
 * Lancia un Error con il dettaglio del backend se l'upload fallisce.
 */
export async function uploadSignatureImage(file: File): Promise<string> {
  const formData = new FormData()
  formData.append('file', file)
  return postSignatureImage(IMAGES_PATH, formData)
}

/**
 * Importa sul nostro storage un'immagine http(s) esterna incollata nella firma
 * (#262): il download avviene lato backend, con protezione SSRF.
 * Restituisce l'URL da usare come src, come `uploadSignatureImage`.
 */
export async function importSignatureImage(url: string): Promise<string> {
  return postSignatureImage(`${IMAGES_PATH}/import`, JSON.stringify({ url }), {
    'Content-Type': 'application/json',
  })
}

/** L'immagine è già sul nostro storage (URL relativo o assoluto, anche da un'altra pagina dell'app). */
export function isSignatureImageUrl(src: string): boolean {
  return [API_BASE, window.location.origin].some((base) =>
    src.startsWith(`${base}${IMAGES_PATH}/`),
  )
}

/** Oltre questo tempo l'immagine resta senza width di default: il salvataggio non resta bloccato. */
export const MEASURE_TIMEOUT_MS = 5000
/** Upload o import appeso: fallisce come gli altri errori, così il salvataggio si sblocca (#263). */
export const REQUEST_TIMEOUT_MS = 30_000

/** Larghezza naturale dell'immagine in `src` (#263), o null se non si carica. */
export function measureImageWidth(src: string): Promise<number | null> {
  return new Promise((resolve) => {
    const img = new Image()
    const timer = setTimeout(() => done(null), MEASURE_TIMEOUT_MS)
    const done = (width: number | null) => {
      clearTimeout(timer)
      img.onload = img.onerror = null
      img.src = ''
      resolve(width)
    }
    img.onload = () => done(img.naturalWidth || null)
    img.onerror = () => done(null)
    img.src = src
  })
}

async function postSignatureImage(
  path: string,
  body: BodyInit,
  headers: Record<string, string> = {},
): Promise<string> {
  const token = localStorage.getItem('access_token')
  let res: Response
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, ...headers },
      body,
      signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
    })
  } catch (error) {
    // Il messaggio del browser ("signal timed out") non è per l'utente: vale
    // il messaggio generico di upload fallito.
    if (error instanceof DOMException && error.name === 'TimeoutError') throw new Error('')
    throw error
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error(err.detail || '')
  }
  const data = await res.json()
  return `${API_BASE}${data.url}`
}
